import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

const focusAgentInputSchema = z.object({
  agentId: z.string().uuid(),
});

type Params = z.infer<typeof focusAgentInputSchema>;

export class FocusAgentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Focus on Agent';
  inputSchema = focusAgentInputSchema;
  group = 'Chat';
  description = 'Prioritize using this agent for tasks';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const result = await this.client.addAction({
      conversationId: model.conversationId,
      action: { type: 'focus_agent', agentId: params.agentId },
    });

    if (result.isErr()) {
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to focus agent';
      return err([{ t: 'error', message: errorMessage }]);
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.AgentFocused, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
      agentId: payload.agentId,
    });
  }
}
